import { getVoiceGatewayWsUrl, buildSessionUpdate, getVoiceGatewayConfig } from './voiceGatewayConfig'
import { resample, floatToAlaw, alawToFloat, toBase64, fromBase64 } from './g711'

const CAPTURE_RATE = 8000
const OUTPUT_RATE = 8000
const CHUNK = 2048

export function createGatewaySession() {
  let ws = null
  let closing = false
  let micMuted = false
  let canSendAudio = false
  let currentScriptId = ''
  let capCtx = null
  let capStream = null
  let capNode = null
  let capSink = null
  let playCtx = null
  let nextPlayTime = 0
  let connectSeq = 0
  const liveSources = new Set()
  const listeners = {
    status: [],
    voiceStatus: [],
    transcript: [],
    error: [],
    hangup: [],
  }

  function emit(event, payload) {
    ;(listeners[event] || []).forEach((fn) => {
      try {
        fn(payload)
      } catch (e) {
        /* noop */
      }
    })
  }

  function ensurePlayCtx() {
    if (playCtx) return playCtx
    playCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: OUTPUT_RATE })
    return playCtx
  }

  function unlockAudio() {
    const ctx = ensurePlayCtx()
    if (ctx && ctx.state === 'suspended') ctx.resume()
  }

  function playFrame(u8) {
    const ctx = ensurePlayCtx()
    if (ctx.state === 'suspended') ctx.resume()
    const f32 = resample(alawToFloat(u8), OUTPUT_RATE, ctx.sampleRate)
    const buf = ctx.createBuffer(1, f32.length, ctx.sampleRate)
    buf.copyToChannel(f32, 0)
    const src = ctx.createBufferSource()
    src.buffer = buf
    src.connect(ctx.destination)
    const now = ctx.currentTime
    if (nextPlayTime < now + 0.02) nextPlayTime = now + 0.08
    src.start(nextPlayTime)
    nextPlayTime += buf.duration
    liveSources.add(src)
    src.onended = () => liveSources.delete(src)
  }

  function stopPlayback() {
    liveSources.forEach((s) => {
      try {
        s.stop()
      } catch (e) {
        /* noop */
      }
    })
    liveSources.clear()
    nextPlayTime = 0
  }

  async function startCapture() {
    if (capNode) return
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error('当前浏览器无法使用麦克风')
    }
    capStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    })
    capCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: CAPTURE_RATE })
    await capCtx.resume()
    const src = capCtx.createMediaStreamSource(capStream)
    capNode = capCtx.createScriptProcessor(CHUNK, 1, 1)
    capNode.onaudioprocess = (e) => {
      if (micMuted || !canSendAudio || !ws || ws.readyState !== WebSocket.OPEN) return
      const raw = e.inputBuffer.getChannelData(0)
      const alaw = floatToAlaw(resample(raw, capCtx.sampleRate, CAPTURE_RATE))
      ws.send(JSON.stringify({ type: 'input_audio_buffer.append', audio: toBase64(alaw) }))
    }
    capSink = capCtx.createGain()
    capSink.gain.value = 0
    src.connect(capNode)
    capNode.connect(capSink)
    capSink.connect(capCtx.destination)
  }

  function stopCapture() {
    canSendAudio = false
    if (capNode) {
      try {
        capNode.disconnect()
      } catch (e) {
        /* noop */
      }
      capNode = null
    }
    if (capSink) {
      try {
        capSink.disconnect()
      } catch (e) {
        /* noop */
      }
      capSink = null
    }
    if (capCtx) {
      try {
        capCtx.close()
      } catch (e) {
        /* noop */
      }
      capCtx = null
    }
    if (capStream) {
      capStream.getTracks().forEach((t) => t.stop())
      capStream = null
    }
  }

  function handleEvent(ev) {
    const t = ev && ev.type
    if (t === 'response.audio.delta') {
      if (ev.delta) playFrame(fromBase64(ev.delta))
      return
    }
    switch (t) {
      case 'session.created':
        ws.send(JSON.stringify(buildSessionUpdate({ scriptId: currentScriptId })))
        break
      case 'session.updated':
        emit('status', 'connected')
        emit('voiceStatus', 'calling')
        canSendAudio = true
        startCapture().catch((err) => {
          emit('error', err && err.message ? err.message : '麦克风启动失败')
        })
        break
      case 'conversation.item.input_audio_transcription.delta':
        emit('transcript', {
          role: 'user',
          key: 'u:' + (ev.item_id || ''),
          text: ev.delta || '',
          final: false,
          replace: false,
        })
        break
      case 'conversation.item.input_audio_transcription.completed':
        emit('transcript', {
          role: 'user',
          key: 'u:' + (ev.item_id || ''),
          text: ev.transcript || '',
          final: true,
          replace: true,
        })
        break
      case 'response.audio_transcript.delta':
        emit('transcript', {
          role: 'bot',
          key: 'a:' + (ev.response_id || '') + ':' + (ev.item_id || ''),
          text: ev.delta || '',
          final: false,
          replace: false,
        })
        break
      case 'response.audio_transcript.done':
        emit('transcript', {
          role: 'bot',
          key: 'a:' + (ev.response_id || '') + ':' + (ev.item_id || ''),
          text: ev.transcript || '',
          final: true,
          replace: true,
        })
        break
      case 'response.output_audio_buffer.clear':
        stopPlayback()
        break
      case 'error':
        emit('error', (ev.error && ev.error.message) || '语音网关错误')
        break
      case 'session.closed':
        cleanup({ remote: !closing, socket: ws })
        break
      default:
        break
    }
  }

  function detachSocket(socket) {
    if (!socket) return
    socket.onopen = null
    socket.onmessage = null
    socket.onerror = null
    socket.onclose = null
  }

  function waitSocketClosed(socket, ms) {
    return new Promise((resolve) => {
      if (!socket || socket.readyState === WebSocket.CLOSED) {
        resolve()
        return
      }
      let settled = false
      const finish = () => {
        if (settled) return
        settled = true
        resolve()
      }
      socket.addEventListener('close', finish)
      setTimeout(() => {
        try {
          socket.close()
        } catch (e) {
          /* noop */
        }
        setTimeout(finish, 400)
      }, ms)
    })
  }

  function cleanup({ remote, silent, socket } = {}) {
    const target = socket || ws
    const wasClosing = closing
    closing = true
    canSendAudio = false
    stopCapture()
    stopPlayback()
    detachSocket(target)
    if (target) {
      try {
        if (target.readyState !== WebSocket.CLOSED) target.close()
      } catch (e) {
        /* noop */
      }
    }
    if (ws === target) ws = null
    if (!silent) {
      emit('voiceStatus', 'idle')
      emit('status', 'disconnected')
      if (remote && !wasClosing) emit('hangup', { by: 'remote' })
    }
  }

  function hangup({ silent = false } = {}) {
    const socket = ws
    closing = true
    canSendAudio = false
    if (!socket) {
      cleanup({ remote: false, silent })
      return Promise.resolve()
    }
    if (socket.readyState === WebSocket.OPEN) {
      try {
        socket.send(JSON.stringify({ type: 'call.hangup' }))
      } catch (e) {
        /* noop */
      }
    }
    return waitSocketClosed(socket, 800).then(() => {
      cleanup({ remote: false, silent, socket })
    })
  }

  function disconnect({ silent = false } = {}) {
    return hangup({ silent })
  }

  function connect({ scriptId } = {}) {
    const seq = (connectSeq += 1)
    return hangup({ silent: true }).then(() => {
      if (seq !== connectSeq) return
      closing = false
      micMuted = false
      canSendAudio = false
      const cfg = getVoiceGatewayConfig()
      if (!cfg.apiKey || !cfg.host) {
        emit('error', '语音网关未配置')
        emit('status', 'disconnected')
        return
      }
      currentScriptId = scriptId
      emit('status', 'connecting')
      emit('voiceStatus', 'connecting')
      const url = getVoiceGatewayWsUrl()
      let socket
      try {
        socket = new WebSocket(url)
      } catch (e) {
        emit('error', '无法连接语音测试网关')
        emit('status', 'disconnected')
        emit('voiceStatus', 'error')
        return
      }
      if (seq !== connectSeq) {
        try {
          socket.close()
        } catch (e) {
          /* noop */
        }
        return
      }
      ws = socket
      socket.binaryType = 'arraybuffer'
      socket.onopen = () => {}
      socket.onerror = () => {
        if (ws !== socket) return
        emit('error', '语音网关连接失败')
      }
      socket.onclose = () => {
        if (ws !== socket) return
        if (!closing) cleanup({ remote: true, socket })
        else cleanup({ remote: false, silent: true, socket })
      }
      socket.onmessage = (e) => {
        if (ws !== socket) return
        try {
          handleEvent(JSON.parse(e.data))
        } catch (err) {
          /* noop */
        }
      }
    })
  }

  return {
    connect,
    disconnect,
    hangup,
    unlockAudio,
    setMicMuted(muted) {
      micMuted = !!muted
    },
    stopPlayback,
    on(event, fn) {
      if (!listeners[event]) listeners[event] = []
      listeners[event].push(fn)
      return () => {
        listeners[event] = (listeners[event] || []).filter((x) => x !== fn)
      }
    },
  }
}
